import {
  COUNTY_THRESHOLDS,
  fillFor,
  fillForMargin,
  formatMargin,
  formatPercent,
  formatVotes,
  hatchFor,
  missHatch,
  NO_DATA,
  NO_RACE,
  RAMPS,
  SHIFT_THRESHOLDS,
  STRENGTH_LABEL,
  TOSSUP,
} from "./colors";
import { DISTRICTS } from "./districts";
import { STATES } from "./geo";
import {
  callFor,
  type County2024,
  decidedParty,
  type EstimateContext,
  estimateCounties,
  estimateOffice,
  EXTERNAL_SIGMA,
  externalForecast,
  externalProbability,
  forecastFor,
  isFlip,
  layerForecast,
  leftParty,
  measureSwing,
  nationalSwing,
  projectDistrict,
  simulateHouse,
  simulateSenate,
  type StateSwing,
} from "./model";
import { shortName } from "./survey";
import type {
  Call,
  District,
  DistrictProjection,
  ExternalRow,
  ExternalSource,
  ForesightEstimate,
  Layer,
  ModelSettings,
  Office,
  Paint,
  Party,
  Race,
  RaceForecast,
  SeatProjection,
  StateInfo,
  StateSample,
} from "./types";

/* ── Rows ──────────────────────────────────────────
 * One row per race for the selected office (one per state for the House
 * generic ballot), combining the Foresight read, the external source and
 * the active layer's call. Everything the map, list and seat bar show.
 */

export interface RaceRow {
  call: Call;
  estimate: ForesightEstimate | null;
  external: ExternalRow | null;
  /** The external source's left-side odds, when it has this race. */
  externalProbability: number | null;
  flip: boolean;
  forecast: RaceForecast | null;
  leftParty: Party;
  race: Race | null;
  sample: StateSample | null;
  state: StateInfo;
}

/** Gap between Foresight and the external odds, in probability points
    (two steps per side, matching the legend). */
const DIFFERENCE_THRESHOLDS: [number, number, number] = [5, 15, Infinity];

export const OFFICE_LABEL: Record<Office, string> = {
  governor: "Governor",
  house: "House",
  senate: "Senate",
};

export const describeSide = (
  race: Race | null,
  side: "left" | "right"
): string => {
  if (!race) return side === "left" ? "Democrats" : "Republicans";
  const candidate = race[side];
  if (!candidate)
    return side === "left" ? "Democrat (TBD)" : "Republican (TBD)";
  return `${candidate.name} (${candidate.party})`;
};

export const buildRows = ({
  county2024,
  edge,
  environment,
  external,
  layer,
  office,
  races,
  samples,
  settings,
}: {
  county2024: County2024 | null;
  /** `turnoutEdge` of these samples. */
  edge: number;
  /** `nationalEnvironment` of these samples at these settings. */
  environment: number | null;
  external: ExternalSource | null;
  layer: Layer;
  office: Office;
  races: Race[];
  samples: Record<string, StateSample>;
  settings: ModelSettings;
}): RaceRow[] => {
  const units: { race: Race | null; state: StateInfo }[] =
    office === "house"
      ? STATES.filter((s) => s.abbr !== "DC").map((state) => ({
          race: null,
          state,
        }))
      : races
          .filter((r) => r.office === office)
          .flatMap((race) => {
            const state = STATES.find((s) => s.abbr === race.state);
            return state ? [{ race, state }] : [];
          });

  return units.map(({ race, state }) => {
    const sample = samples[state.abbr] ?? null;
    const context: EstimateContext = {
      county2024,
      edge,
      environment,
      settings,
      state,
    };
    const estimate =
      sample && sample.offices.includes(office)
        ? estimateOffice(sample, office, context, race)
        : null;
    const ext = external ? externalRow(external, office, state.abbr) : null;
    const party = race ? leftParty(race) : "D";
    const forecast = forecastFor(
      layer === "difference" ? "foresight" : layer,
      race,
      estimate,
      ext,
      settings,
      false
    );
    const call = callFor(forecast, party);
    return {
      call,
      estimate,
      external: ext,
      externalProbability: ext ? externalProbability(ext) : null,
      flip: race ? isFlip(call, race) : false,
      forecast,
      leftParty: party,
      race,
      sample,
      state,
    };
  });
};

/** A source's statewide row; for the House without one, the mean margin of
    the state's districts that have a margin. */
const externalRow = (
  source: ExternalSource,
  office: Office,
  state: string
): ExternalRow | null => {
  const row = source.rows.find(
    (r) => r.office === office && r.state === state && !r.district
  );
  if (row || office !== "house") return row ?? null;
  const margins = source.rows.flatMap((r) =>
    r.office === "house" && r.state === state && r.district && r.margin !== null
      ? [r.margin]
      : []
  );
  return margins.length > 0
    ? {
        leftProbability: null,
        margin: margins.reduce((sum, m) => sum + m, 0) / margins.length,
        office,
        state,
      }
    : null;
};

/* ── National map ──────────────────────────────── */

const raceTitle = (row: RaceRow, office: Office) =>
  `${row.state.name} · ${OFFICE_LABEL[office]}${row.race?.special ? " (special)" : ""}`;

const tooltipFor = (
  row: RaceRow,
  office: Office,
  sourceLabel: string | null
) => {
  const lines = [raceTitle(row, office)];
  if (row.race) {
    lines.push(
      `${describeSide(row.race, "left")} vs ${describeSide(row.race, "right")}`
    );
  }
  if (row.estimate) {
    lines.push(
      `Foresight: ${formatMargin(row.estimate.margin, row.leftParty)} · ${formatPercent(row.estimate.leftProbability)} ${row.leftParty} win`
    );
  } else {
    lines.push("Foresight: not surveyed yet");
  }
  if (sourceLabel) {
    lines.push(
      `${sourceLabel}: ${
        row.externalProbability === null
          ? "no data"
          : `${formatPercent(row.externalProbability)} ${row.leftParty} win`
      }`
    );
  }
  if (row.race) {
    const held = `Held by ${row.race.incumbentParty}`;
    lines.push(
      row.flip ? `${held} · projected FLIP to ${row.call.leader}` : held
    );
  }
  return lines.join("\n");
};

export const nationalPaints = (
  rows: RaceRow[],
  layer: Layer,
  office: Office,
  sourceLabel: string | null,
  /** The compared source is the count. */
  results: boolean
): Record<string, Paint> => {
  const paints: Record<string, Paint> = {};
  for (const row of rows) {
    if (results) {
      const verdict = raceVerdict(row);
      const head = [raceTitle(row, office)];
      if (row.race) {
        head.push(
          `${describeSide(row.race, "left")} vs ${describeSide(row.race, "right")}`
        );
      }
      paints[row.state.fips] = resultPaint(
        layer,
        verdict,
        row.leftParty,
        [
          ...head,
          ...resultLines(verdict, row.estimate, row.external, [
            row.leftParty,
            "R",
          ]),
        ].join("\n")
      );
      continue;
    }
    const tooltip = tooltipFor(row, office, sourceLabel);
    if (layer === "difference") {
      const gap =
        row.estimate && row.externalProbability !== null
          ? (row.estimate.leftProbability - row.externalProbability) * 100
          : null;
      // The left side is an independent in a few Senate races (NE, ID...).
      const left = row.leftParty === "I" ? "pro-independent" : "Democratic";
      paints[row.state.fips] = {
        dashed: gap === null,
        fill:
          gap === null
            ? NO_DATA
            : fillForMargin(gap, DIFFERENCE_THRESHOLDS, row.leftParty),
        hatch: null,
        muted: false,
        tooltip:
          gap === null
            ? tooltip
            : `${tooltip}\nForesight is ${Math.abs(gap).toFixed(0)} pts more ${gap > 0 ? left : "Republican"}`,
      };
      continue;
    }
    const noData = row.call.leftProbability === null;
    paints[row.state.fips] = {
      dashed: noData,
      fill: fillFor(row.call),
      hatch: row.flip ? hatchFor(row.call) : null,
      muted: false,
      tooltip: noData
        ? tooltip
        : `${tooltip}\n${STRENGTH_LABEL[row.call.strength]}${row.call.leader && row.call.strength !== "tossup" ? ` ${row.call.leader}` : ""}`,
    };
  }
  return paints;
};

/* ── Seats ─────────────────────────────────────── */

/** Senate seats not up in 2026, by caucus (independents counted with D). */
export const SENATE_HOLDOVERS: Record<Party, number> = { D: 34, I: 0, R: 31 };
/** Governors not up in 2026. */
export const GOVERNOR_HOLDOVERS: Record<Party, number> = { D: 6, I: 0, R: 8 };

export const senateProjection = (
  rows: RaceRow[],
  layer: Layer,
  settings: ModelSettings
): SeatProjection =>
  simulateSenate(
    rows.flatMap((row) => {
      if (!row.race) return [];
      const forecast =
        forecastFor(
          layer === "difference" ? "foresight" : layer,
          row.race,
          row.estimate,
          row.external,
          settings,
          true
        ) ??
        // No data and no rating: assume the seat holds.
        ({
          leftProbability: row.race.incumbentParty === "R" ? 0.1 : 0.9,
          margin: null,
          sigma: 6,
          source: "prior",
        } satisfies RaceForecast);
      return [{ forecast, leftParty: row.leftParty }];
    }),
    SENATE_HOLDOVERS
  );

/* ── House districts ───────────────────────────── */

export interface DistrictRow {
  call: Call;
  /** Party winning whatever happens (one-party finalists, unopposed). */
  decided: Party | null;
  district: District;
  external: ExternalRow | null;
  externalProbability: number | null;
  flip: boolean;
  /** The active layer's read; null for decided seats. */
  forecast: RaceForecast | null;
  /** Foresight's read; null for decided seats or before any survey. */
  projection: DistrictProjection | null;
}

export const districtSides = (district: District): [Party, Party] => [
  district.left?.party ?? "D",
  district.right?.party ?? "R",
];

/** Every district as drawn for 2026, read under the active layer. */
export const buildDistrictRows = ({
  county2024,
  edge,
  external,
  layer,
  samples,
  settings,
}: {
  county2024: County2024 | null;
  /** `turnoutEdge` of these samples. */
  edge: number;
  external: ExternalSource | null;
  layer: Layer;
  samples: Record<string, StateSample>;
  settings: ModelSettings;
}): DistrictRow[] => {
  const swings: Record<string, StateSwing> = {};
  const reads: { state: StateInfo; swing: StateSwing }[] = [];
  for (const state of STATES) {
    const sample = samples[state.abbr];
    if (!sample || !sample.offices.includes("house")) continue;
    const context: EstimateContext = {
      county2024,
      edge,
      environment: null,
      settings,
      state,
    };
    const estimate = estimateOffice(sample, "house", context, null);
    if (!estimate) continue;
    const swing = measureSwing(sample, context, estimate);
    swings[state.abbr] = swing;
    reads.push({ state, swing });
  }
  const national = nationalSwing(reads);
  const read = layer === "difference" ? "foresight" : layer;

  return DISTRICTS.map((district): DistrictRow => {
    const ext = external?.rows.find((r) => r.district === district.id) ?? null;
    const externalP = ext ? externalProbability(ext) : null;
    const decided = decidedParty(district);
    if (decided) {
      return {
        call: {
          leader: decided,
          leftProbability: decided === "R" ? 0 : 1,
          margin: null,
          strength: "safe",
        },
        decided,
        district,
        external: ext,
        externalProbability: externalP,
        flip: decided !== district.holder,
        forecast: null,
        projection: null,
      };
    }

    const own = swings[district.state];
    const projection = own
      ? projectDistrict(district, own, "counties")
      : national
        ? projectDistrict(district, national, "national")
        : null;
    const forecast = layerForecast(
      read,
      projection && {
        leftProbability: projection.leftProbability,
        margin: projection.margin,
        sigma: projection.sigma,
        source: "foresight",
      },
      externalForecast(ext),
      settings
    );
    const call = callFor(forecast, ...districtSides(district));
    return {
      call,
      decided: null,
      district,
      external: ext,
      externalProbability: externalP,
      flip: call.leader !== null && call.leader !== district.holder,
      forecast,
      projection,
    };
  });
};

/** House seat odds under the active layer; null when it reads no
    contested seat (Foresight before any survey). Seats it misses keep
    their holder as a placeholder. */
export const houseProjection = (rows: DistrictRow[]): SeatProjection | null => {
  if (!rows.some((row) => row.forecast)) return null;
  return simulateHouse(
    rows.map((row) => {
      const [left, right] = districtSides(row.district);
      return {
        decided: row.decided,
        forecast: row.decided
          ? null
          : (row.forecast ?? {
              leftProbability: row.district.holder === left ? 0.9 : 0.1,
              margin: null,
              sigma: EXTERNAL_SIGMA,
              source: "prior",
            }),
        leftParty: left,
        rightParty: right,
        state: row.district.state,
        // Only a read that includes Foresight's shares its state survey error.
        stateSigma:
          row.projection && row.forecast && row.forecast.source !== "external"
            ? row.projection.stateSigma
            : 0,
      };
    })
  );
};

export const governorCounts = (rows: RaceRow[]) => {
  const counts = { ...GOVERNOR_HOLDOVERS, open: 0 };
  for (const row of rows) {
    if (row.call.leader && row.call.strength !== "tossup")
      counts[row.call.leader]++;
    else counts.open++;
  }
  return counts;
};

/* ── County map ────────────────────────────────── */

export type CountyLayer = "2024" | "foresight" | "shift";

const MUTED_BELOW = 5;

export const countyPaints = ({
  county2024,
  edge,
  environment,
  layer,
  office,
  row,
  settings,
}: {
  county2024: County2024 | null;
  edge: number;
  environment: number | null;
  layer: CountyLayer;
  office: Office;
  row: RaceRow;
  settings: ModelSettings;
}): Record<string, Paint> => {
  const paints: Record<string, Paint> = {};
  const harrisMargin = (fips: string) => {
    const share = county2024?.[fips];
    return share === undefined ? null : (2 * share - 1) * 100;
  };

  if (layer === "2024") {
    for (const [fips, share] of Object.entries(county2024 ?? {})) {
      if (!fips.startsWith(row.state.fips)) continue;
      const margin = (2 * share - 1) * 100;
      paints[fips] = {
        dashed: false,
        fill: fillForMargin(margin, COUNTY_THRESHOLDS),
        hatch: null,
        muted: false,
        tooltip: `2024 result: ${formatMargin(margin).replace("D", "Harris ").replace("R", "Trump ")}`,
      };
    }
    return paints;
  }

  if (!row.sample || !row.estimate) return paints;
  const counties = estimateCounties(
    row.sample,
    office,
    { county2024, edge, environment, settings, state: row.state },
    row.estimate
  );
  for (const county of counties) {
    const margin = (2 * county.leftShare - 1) * 100;
    const past = harrisMargin(county.fips);
    const shift = past === null ? null : margin - past;
    // The county map adds the name of the shape it draws (Connecticut's
    // planning regions are pooled into old counties).
    const lines = [
      `Foresight: ${formatMargin(margin, row.leftParty)} · ${county.n} respondent${county.n === 1 ? "" : "s"}`,
      past === null ? null : `2024: ${formatMargin(past)}`,
      shift === null ? null : `Shift vs 2024: ${formatMargin(shift)}`,
      county.n < MUTED_BELOW
        ? `Thin sample: pulled toward ${past === null ? "the state read" : "its 2024 result plus the state swing"}`
        : null,
    ].filter((line): line is string => line !== null);

    if (layer === "shift") {
      paints[county.fips] = {
        dashed: shift === null,
        fill: shift === null ? NO_DATA : fillForMargin(shift, SHIFT_THRESHOLDS),
        hatch: null,
        muted: county.n < MUTED_BELOW,
        tooltip: lines.join("\n"),
      };
      continue;
    }
    paints[county.fips] = {
      dashed: false,
      fill: fillForMargin(margin, COUNTY_THRESHOLDS, row.leftParty),
      hatch: null,
      muted: county.n < MUTED_BELOW,
      tooltip: lines.join("\n"),
    };
  }
  return paints;
};

/* ── Results ───────────────────────────────────────
 * With the count compared, every race is a call: the party Foresight's read
 * favors next to the party the race is called for, and Foresight's margin
 * next to the count's, both in points of the two-party vote.
 */

/** Foresight's call next to the count. */
export interface Verdict {
  /** Party the race is called for; null until it is. */
  actual: Party | null;
  /** Foresight's margin minus the count's (positive: Foresight leaned
      further to the left side); null without both. */
  gap: number | null;
  /** Called for another party than Foresight's. */
  miss: boolean;
  /** Party Foresight's read favors; null without a read. */
  ours: Party | null;
}

/** How Foresight's calls fared against the count, for one office. */
export interface ResultsTally {
  /** Seats per party among called races, holdovers included. */
  actual: Record<Party, number>;
  /** Called races Foresight had a call on. */
  judged: number;
  /** Races behind `republicanGap`. */
  measured: number;
  /** Labels of the called races Foresight called for the other party. */
  misses: string[];
  /** Seats per party by Foresight's calls, holdovers included. */
  ours: Record<Party, number>;
  /** Mean points by which Foresight's margin was more Republican than the
      count's (negative: less); null without a race to measure. */
  republicanGap: number | null;
  /** Races not called yet. */
  uncalled: number;
  /** Races without a Foresight read. */
  unread: number;
}

type Read = Pick<ForesightEstimate, "leftProbability" | "margin">;

/** Margin points within which Foresight matched the count, then the larger
    misses (two steps per side, matching the legend). */
export const RESULT_GAP_THRESHOLDS: [number, number, number] = [2, 5, Infinity];

const lastName = (name: string) =>
  name
    .replace(/,? (Jr\.|Sr\.|II|III|IV)$/, "")
    .split(" ")
    .pop() ?? name;

/** "McBath (D) vs Smith (R)", or "McBath (D), unopposed". */
export const districtMatchup = (district: District): string => {
  const [left, right] = [district.left, district.right].map((c) =>
    c ? `${shortName(c)} (${c.party})` : null
  );
  return left && right ? `${left} vs ${right}` : `${left ?? right}, unopposed`;
};

/** "5.9 pts more R": how far Foresight leaned from the count. */
export const gapText = (gap: number, left: Party, right: Party = "R") =>
  Math.abs(gap) < 0.05
    ? "on the count"
    : `${Math.abs(gap).toFixed(1)} pts more ${gap > 0 ? left : right}`;

const verdictOf = (
  read: Read | null,
  external: ExternalRow | null,
  [left, right]: [Party, Party],
  decided: Party | null
): Verdict => {
  const ours =
    decided ??
    (read === null || read.leftProbability === 0.5
      ? null
      : read.leftProbability > 0.5
        ? left
        : right);
  const called = external?.result?.called ?? null;
  const actual = called === "left" ? left : called === "right" ? right : null;
  const margin = external?.margin ?? null;
  return {
    actual,
    gap: read && margin !== null ? read.margin - margin : null,
    miss: ours !== null && actual !== null && ours !== actual,
    ours,
  };
};

export const raceVerdict = (row: RaceRow): Verdict =>
  verdictOf(row.estimate, row.external, [row.leftParty, "R"], null);

export const districtVerdict = (row: DistrictRow): Verdict =>
  verdictOf(
    row.projection,
    row.external,
    districtSides(row.district),
    row.decided
  );

/** A race painted with the count compared: Foresight's call or the race's
    (grey until called), striped where Foresight called it wrong; or the
    margin gap. */
const resultPaint = (
  layer: Layer,
  verdict: Verdict,
  leftParty: Party,
  tooltip: string
): Paint => {
  if (layer === "difference") {
    return {
      dashed: verdict.gap === null,
      fill:
        verdict.gap === null
          ? NO_DATA
          : fillForMargin(verdict.gap, RESULT_GAP_THRESHOLDS, leftParty),
      hatch: null,
      muted: false,
      tooltip,
    };
  }
  const count = layer === "external";
  const party = count ? verdict.actual : verdict.ours;
  const fill = party ? RAMPS[party].likely : count ? TOSSUP : NO_DATA;
  return {
    dashed: !party && !count,
    fill,
    hatch: verdict.miss ? missHatch(fill) : null,
    muted: false,
    tooltip,
  };
};

/** Tooltip lines with the count compared: Foresight's call, the count with
    each side's votes, and the gap. */
const resultLines = (
  verdict: Verdict,
  read: Read | null,
  external: ExternalRow | null,
  [left, right]: [Party, Party]
): string[] => {
  const lines = [
    read
      ? `Our call: ${verdict.ours ?? "even"} · ${formatMargin(read.margin, left, right)}`
      : verdict.ours
        ? `Our call: ${verdict.ours} (decided)`
        : "Our call: none, no read",
  ];
  const result = external?.result;
  if (!external || !result) return [...lines, "Count: none yet"];
  const sides = [result.left, result.right].flatMap((c) =>
    c ? [`${lastName(c.name)} ${formatVotes(c.votes)}`] : []
  );
  lines.push(
    `Count: ${[formatMargin(external.margin, left, right), ...sides].join(" · ")}`,
    `${result.reporting}% in · ${verdict.actual ? `called ${verdict.actual}` : "not called"}`
  );
  if (verdict.gap !== null) {
    lines.push(`Foresight was ${gapText(verdict.gap, left, right)}`);
  }
  if (verdict.miss) lines.push("Missed call");
  return lines;
};

/** District map fills under the active layer. */
export const districtPaints = (
  rows: DistrictRow[],
  layer: Layer,
  sourceLabel: string | null,
  /** The compared source is the count. */
  results: boolean
): Record<string, Paint> => {
  const paints: Record<string, Paint> = {};
  for (const row of rows) {
    const { decided, district, projection } = row;
    const sides = districtSides(district);
    const lines = [
      `${district.id} · ${districtMatchup(district)}`,
      `Held by ${district.holder} · ${formatMargin((2 * district.harris2024 - 1) * 100)} in 2024`,
    ];

    if (results) {
      const verdict = districtVerdict(row);
      paints[district.id] = resultPaint(
        layer,
        verdict,
        sides[0],
        [
          ...lines,
          ...resultLines(verdict, projection, row.external, sides),
        ].join("\n")
      );
      continue;
    }

    if (decided) {
      lines.push(`Decided: ${decided} either way`);
    } else {
      lines.push(
        projection
          ? `Foresight: ${formatMargin(projection.margin, ...sides)} · ${formatPercent(projection.leftProbability)} ${sides[0]} win`
          : "Foresight: no read yet"
      );
      if (sourceLabel) {
        lines.push(
          `${sourceLabel}: ${row.externalProbability === null ? "no data" : `${formatPercent(row.externalProbability)} ${sides[0]} win`}`
        );
      }
    }

    if (layer === "difference") {
      const gap =
        projection && row.externalProbability !== null
          ? (projection.leftProbability - row.externalProbability) * 100
          : null;
      paints[district.id] = {
        dashed: !decided && gap === null,
        fill: decided
          ? NO_RACE
          : gap === null
            ? NO_DATA
            : fillForMargin(gap, DIFFERENCE_THRESHOLDS, sides[0]),
        hatch: null,
        muted: false,
        tooltip:
          gap === null
            ? lines.join("\n")
            : `${lines.join("\n")}\nForesight is ${Math.abs(gap).toFixed(0)} pts more ${gap > 0 ? sides[0] : sides[1]}`,
      };
      continue;
    }

    const noData = row.call.leftProbability === null;
    paints[district.id] = {
      dashed: noData,
      fill: fillFor(row.call),
      hatch: row.flip ? hatchFor(row.call) : null,
      muted: false,
      tooltip: noData
        ? lines.join("\n")
        : `${lines.join("\n")}\n${STRENGTH_LABEL[row.call.strength]}${row.call.leader && row.call.strength !== "tossup" ? ` ${row.call.leader}` : ""}${row.flip ? ` · flips from ${district.holder}` : ""}`,
    };
  }
  return paints;
};

const tally = (
  items: { label: string; rightParty: Party; verdict: Verdict }[],
  holdovers: Record<Party, number>
): ResultsTally => {
  const actual = { ...holdovers };
  const ours = { ...holdovers };
  const misses: string[] = [];
  let judged = 0;
  let measured = 0;
  let republicanSum = 0;
  let uncalled = 0;
  let unread = 0;
  for (const { label, rightParty, verdict } of items) {
    if (verdict.actual) actual[verdict.actual]++;
    else uncalled++;
    if (verdict.ours) ours[verdict.ours]++;
    else unread++;
    if (verdict.actual && verdict.ours) {
      judged++;
      if (verdict.miss) misses.push(label);
    }
    // Only a Republican right side makes the gap a Republican lean.
    if (verdict.gap !== null && rightParty === "R") {
      measured++;
      republicanSum -= verdict.gap;
    }
  }
  return {
    actual,
    judged,
    measured,
    misses,
    ours,
    republicanGap: measured > 0 ? republicanSum / measured : null,
    uncalled,
    unread,
  };
};

export const tallyRaces = (
  rows: RaceRow[],
  holdovers: Record<Party, number>
): ResultsTally =>
  tally(
    rows.map((row) => ({
      label: row.state.name,
      rightParty: "R",
      verdict: raceVerdict(row),
    })),
    holdovers
  );

export const tallyDistricts = (rows: DistrictRow[]): ResultsTally =>
  tally(
    rows.map((row) => ({
      label: row.district.id,
      rightParty: districtSides(row.district)[1],
      verdict: districtVerdict(row),
    })),
    { D: 0, I: 0, R: 0 }
  );
